import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { getPackages, createPackage, getProjectItems, getSuppliers, sendRFQ } from '../utils/api';

const TRADES = ['Structural Steel','Concrete','Masonry','Façade','Roofing','Electrical','HVAC','Fire Protection','Groundworks','Finishes','Other'];
const TRADE_COLORS = {
  'Structural Steel': { color: 'var(--color-info)', bg: '#EFF6FF' },
  'Concrete':         { color: '#7C3AED', bg: '#F5F3FF' },
  'Masonry':          { color: '#92400E', bg: '#FFFBEB' },
  'Façade':           { color: '#0E7490', bg: '#ECFEFF' },
  'Roofing':          { color: '#5B21B6', bg: '#EDE9FE' },
  'Electrical':       { color: 'var(--color-warning)', bg: '#FFFBEB' },
  'HVAC':             { color: 'var(--color-teal)', bg: 'var(--color-teal-light)' },
  'Fire Protection':  { color: 'var(--color-error)', bg: '#FEF2F2' },
  'Groundworks':      { color: 'var(--color-info)', bg: '#EFF6FF' },
  'Finishes':         { color: '#6D28D9', bg: '#EDE9FE' },
  'Other':            { color: 'var(--color-text-mid)', bg: 'var(--color-background)' },
};

function LifecyclePill({ state }) {
  const labels = {
    draft_scope: 'Draft', scope_review: 'Review', ready_for_rfq: 'Ready', rfq_drafted: 'RFQ Draft',
    rfq_approved: 'RFQ Approved', rfq_sent: 'RFQ Sent', awaiting_quotes: 'Awaiting', quotes_received: 'Quotes In',
    bid_leveling: 'Leveling', award_recommendation: 'Recommendation', award_approved: 'Awarded',
    contract_po_pending: 'Contract Pending', closed: 'Closed',
  };
  const label = labels[state] || state || 'Draft';
  const isDraft = !state || state === 'draft_scope' || state === 'draft';
  const isAwarded = state === 'award_approved' || state === 'awarded';
  const isActive = ['rfq_sent', 'awaiting_quotes', 'quotes_received', 'bid_leveling'].includes(state);
  const bg = isDraft ? '#F1F5F9' : isAwarded ? '#ECFDF5' : isActive ? 'var(--color-teal-light)' : '#EFF6FF';
  const color = isDraft ? 'var(--color-text-light)' : isAwarded ? 'var(--color-success)' : isActive ? 'var(--color-teal)' : 'var(--color-info)';
  return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 600, color, background: bg, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{label}</span>;
}

export default function Packages() {
  const { selectedProject, addToast } = useOutletContext();
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showRFQ, setShowRFQ] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [form, setForm] = useState({ name: '', trade_category: 'Concrete', scope_summary: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) { loadPackages(); loadItems(); }
    loadSuppliers();
  }, [selectedProject]);

  async function loadPackages() {
    try {
      const data = await getPackages(selectedProject.id);
      setPackages(data || []);
    } catch (err) { console.error(err); }
  }

  async function loadItems() {
    try {
      const data = await getProjectItems(selectedProject.id);
      setItems(data || []);
    } catch (err) { console.error(err); }
  }

  async function loadSuppliers() {
    try {
      const data = await getSuppliers();
      setSuppliers(data || []);
    } catch (err) { console.error(err); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await createPackage({ ...form, project_id: selectedProject.id });
      setShowNew(false);
      setForm({ name: '', trade_category: 'Concrete', scope_summary: '' });
      loadPackages();
      setMessage({ type: 'success', text: 'Package created' });
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    setLoading(false);
  }

  async function handleSendRFQ(pkg) {
    if (selectedSuppliers.length === 0) { setMessage({ type: 'error', text: 'Select at least one supplier' }); return; }
    setLoading(true);
    try {
      await sendRFQ({ package_id: pkg.id, supplier_ids: selectedSuppliers.map(Number) });
      setMessage({ type: 'success', text: `RFQ sent to ${selectedSuppliers.length} supplier(s)` });
      setShowRFQ(null);
      setSelectedSuppliers([]);
      loadPackages();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    setLoading(false);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Packages</div>
          <div className="page-subtitle">{packages.length} procurement packages</div>
        </div>
        <div className="page-actions">
          <button onClick={() => setShowNew(true)} className="btn btn-primary">+ New Package</button>
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'}` }}>
          {message.text}
        </div>
      )}

      {showNew && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span style={{ fontWeight: 600 }}>New Package</span></div>
          <div className="card-body">
            <form onSubmit={handleCreate}>
              <div className="grid-3" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Package Name</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. HVAC — Level 2" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Trade</label>
                  <select className="form-select" value={form.trade_category} onChange={e => setForm({ ...form, trade_category: e.target.value })}>
                    {TRADES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Scope Summary</label>
                  <input className="form-input" value={form.scope_summary} onChange={e => setForm({ ...form, scope_summary: e.target.value })} placeholder="Brief scope description" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Creating...' : 'Create Package'}</button>
                <button type="button" onClick={() => setShowNew(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRFQ && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Send RFQ — {showRFQ.name}</h3>
              <button onClick={() => setShowRFQ(null)} className="btn btn-ghost btn-sm">✕</button>
            </div>
            <div className="modal-body">
              {suppliers.length === 0 ? (
                <p>No suppliers found. <span style={{ color: 'var(--color-teal)', cursor: 'pointer' }} onClick={() => { setShowRFQ(null); navigate('/suppliers'); }}>Add suppliers first →</span></p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {suppliers.map(s => {
                    const isSelected = selectedSuppliers.includes(s.id.toString());
                    return (
                      <div key={s.id} onClick={() => setSelectedSuppliers(prev => isSelected ? prev.filter(x => x !== s.id.toString()) : [...prev, s.id.toString()])}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: `1px solid ${isSelected ? 'var(--color-teal)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', background: isSelected ? 'var(--color-teal-light)' : 'var(--color-surface)' }}>
                        <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: 'var(--color-teal)' }} />
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-light)' }}>{s.contact_email || s.email}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => { setShowRFQ(null); setSelectedSuppliers([]); }} className="btn btn-secondary">Cancel</button>
              <button onClick={() => handleSendRFQ(showRFQ)} disabled={loading || selectedSuppliers.length === 0} className="btn btn-primary">
                {loading ? 'Sending...' : `Send to ${selectedSuppliers.length} supplier(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {packages.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon">▦</div>
          <h3>No packages yet</h3>
          <p>Create packages to group extracted items by trade</p>
          <button onClick={() => setShowNew(true)} className="btn btn-primary">+ Create Package</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {packages.map(pkg => {
            const trade = pkg.trade_category || pkg.trade;
            const tc = TRADE_COLORS[trade] || TRADE_COLORS['Other'];
            const pkgItems = items.filter(i => i.package_id === pkg.id);
            return (
              <div key={pkg.id} className="card" style={{ borderLeft: `3px solid ${tc.color}`, cursor: 'pointer' }}
                onClick={() => navigate(`/packages/${pkg.id}`)}>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-text-light)', marginBottom: 3 }}>PKG-{String(pkg.id).padStart(3, '0')}</div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{pkg.name}</div>
                    </div>
                    <LifecyclePill state={pkg.lifecycle_state || pkg.status} />
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 11, fontWeight: 500, color: tc.color, background: tc.bg, display: 'inline-block', marginBottom: 10 }}>{trade}</span>
                  <div style={{ fontSize: 13, color: 'var(--color-text-mid)', marginBottom: 12 }}>{pkgItems.length} item{pkgItems.length !== 1 ? 's' : ''}</div>
                  <div style={{ display: 'flex', gap: 6, paddingTop: 12, borderTop: '1px solid var(--color-border)' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => navigate(`/packages/${pkg.id}`)} className="btn btn-secondary btn-sm" style={{ flex: 1 }}>Open →</button>
                    <button onClick={() => { setShowRFQ(pkg); setSelectedSuppliers([]); }} className="btn btn-primary btn-sm" style={{ flex: 1 }}>Send RFQ</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}